import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { Currency } from "app/models/currency.model";
import {
  ComboGroup,
  ComboGroupOverride,
  ComboGroupStructure,
  ComboProductStructure,
  DisplayedComboGroupStructure,
  DisplayedComboProductStructure,
  DisplayedSize, emptyOverride,
  MenuSection,
  NameTranslationType,
  OverrideInstruction,
  OverridePosition,
  OverridePositionRequest, OverridePositionsMap,
  PositionTypeEnum,
  Product
} from 'app/models/menu.model';
import { Organization } from "app/models/organization.model";
import { buildNameTranslations, getComboElementKey, MenuOverrideService } from "app/services/menu-override.service";
import { MenuService } from 'app/services/menu.service';
import {
  buildOverridePositionRequest, hasChangeComboStructure
} from 'app/pages/menu/shared/edit-product-modal/edit-combo-product/edit-combo-product-helper';
import { MatDialog } from '@angular/material/dialog';
import {
  EditModifierGroupsModalComponent
} from 'app/pages/menu/shared/edit-product-modal/edit-combo-group/edit-modifier-groups-modal/edit-modifier-groups-modal.component';
import { ProductApiService } from 'app/services/product-api.service';

// @UntilDestroy()
@Component({
  selector: 'app-edit-combo-group[saveForAllCombo][saveForAllProductsInComboGroup]',
  templateUrl: './edit-combo-group.component.html',
  styleUrls: ['./edit-combo-group.component.scss']
})
export class EditComboGroupComponent implements OnInit {
  isEditingMode = false;
  isSavingMode = false;
  iconTooltip: string;

  @Input() allowOverrides: boolean;
  @Input() comboGroupStructure: DisplayedComboGroupStructure;
  @Input() originalComboGroupStructure: ComboGroupStructure;
  @Input() currency: Currency | null;
  @Input() dictionaryComboGroups: Map<string, ComboGroup>;
  @Input() originalDictionaryComboGroups: Map<string, ComboGroup>;
  @Input() dictionaryProducts: Map<string, Product>;
  @Input() dictionarySizes: Map<string, DisplayedSize>;
  @Input() originalDictionaryProducts: Map<string, Product>;
  @Input() overridePositionsMap: OverridePositionsMap;
  @Input() currentOrganization: Organization;
  @Input() menuSections: MenuSection[];
  @Input() menuSectionId: string;
  @Input() productId: string;
  @Input() saveForMenuSections: string[];
  @Output() changeInformationMessageEvent = new EventEmitter<string[]>();
  @Input() saveForAllCombo: boolean;
  @Input() saveForAllProductsInComboGroup: boolean;

  initialComboProductStructures: DisplayedComboProductStructure[];
  comboProductStructures: DisplayedComboProductStructure[];

  initValues = {};
  changes = [];
  comboGroupName: string;
  nameTranslations: NameTranslationType;
  applyNameChangeToAll: boolean = true;
  nameTranslationKeys: string [];
  comboGroup: ComboGroup;
  originalComboGroup: ComboGroup;

  constructor(
    private translate: TranslateService,
    private menuService: MenuService,
    private dialog: MatDialog,
    private productApiService: ProductApiService,
  ) {
  }

  ngOnInit(): void {
    this.iconTooltip = this.translate.instant('COMMON.EDIT');
    this.comboGroup = this.dictionaryComboGroups.get(this.comboGroupStructure.comboGroupId);
    this.originalComboGroup = this.originalDictionaryComboGroups.get(this.comboGroupStructure.comboGroupId);
    this.comboGroupName = this.comboGroup.name;
    this.nameTranslations = buildNameTranslations(
      this.comboGroup.nameTranslations,
      this.currentOrganization.additionalLanguages);
    this.nameTranslationKeys = Object.keys(this.nameTranslations);
    this.comboProductStructures = this.getNewSortedComboProducts(this.comboGroupStructure.products);

    this.setInitValues();
  }

  setInitValues(){
    this.initValues['comboGroupName'] = this.comboGroup.name;
    this.initValues['nameTranslations'] = {...buildNameTranslations(
        this.comboGroup.nameTranslations,
        this.currentOrganization.additionalLanguages)};
    this.initialComboProductStructures = this.getNewSortedComboProducts(this.comboGroupStructure.products);
  }

  getNewSortedComboProducts(comboProducts: DisplayedComboProductStructure[]) {
    return [...comboProducts].map(p => ({...p})).sort(this.sortComboProductsByOrder)
  }

  sortComboProductsByOrder(p1: DisplayedComboProductStructure, p2: DisplayedComboProductStructure): number {
    if (p1.order > p2.order) {
      return 1;
    }
    if (p1.order < p2.order) {
      return -1;
    }
    return 0;
  }

  async editMode() {
    if(!this.allowOverrides){
      return;
    }
    this.isEditingMode = !this.isEditingMode;
    this.iconTooltip = this.translate.instant(this.isEditingMode ? 'COMMON.SAVE' : 'COMMON.EDIT');
    if (!this.isEditingMode) {
      await this.saveComboGroup();
    }
  }

  async saveComboGroup() {
    this.isSavingMode = true;
    let batchOverridePositionsRequest: OverridePositionRequest[] = [];

    let [comboGroupOverridePosition, comboGroupOverride, isOverrideExists] = this.findOrCreateComboGroupOverridePositionById(
      this.comboGroupStructure.comboGroupId,
      this.menuSectionId,
      this.overridePositionsMap);

    let overrideInstructions = this.createOverrideInstructions(isOverrideExists, comboGroupOverride);

    if (overrideInstructions.length) {
      const primaryOverridePositionRequest: OverridePositionRequest = {
        ...comboGroupOverridePosition,
        instructions: overrideInstructions,
      };

      batchOverridePositionsRequest.push(primaryOverridePositionRequest);

      if (this.applyNameChangeToAll){
        const copiedOverridePositionsRequest = this.copyOverridePositionsRequest(
          this.comboGroupStructure.comboGroupId);
        batchOverridePositionsRequest = [...batchOverridePositionsRequest, ...copiedOverridePositionsRequest];
      }
    }

    const comboProductsStructureRequests = await this.buildComboProductsStructureRequest();

    batchOverridePositionsRequest = [...batchOverridePositionsRequest, ...comboProductsStructureRequests];

    if (batchOverridePositionsRequest.length){
      await this.menuService.saveOverridePositions(this.menuSectionId, batchOverridePositionsRequest, this.saveForMenuSections);
    }

    this.isSavingMode = false;

    this.setInitValues();
  }

  createOverrideInstructions(isOverrideExists: boolean, comboGroupOverride: ComboGroupOverride): OverrideInstruction[]
  {
    const overrideInstructions: OverrideInstruction[] = []
    if (this.initValues['comboGroupName'].trim() !== this.comboGroupName.trim()) {
      MenuOverrideService.overrideTextFiled(this.comboGroup, this.originalComboGroup, 'name', this.comboGroupName, isOverrideExists, overrideInstructions);
    }

    for (const lang in this.nameTranslations) {
      const value = this.nameTranslations[lang];
      if (this.initValues['nameTranslations'][lang] !== value) {
        MenuOverrideService.overrideTranslationFiled(
          this.comboGroup, this.originalComboGroup, 'nameTranslations', lang, value, isOverrideExists, comboGroupOverride, overrideInstructions);
      }
    }

    return overrideInstructions;
  }

  findOrCreateComboGroupOverridePositionById(
    comboGroupId: string,
    menuSectionId: string,
    overridePositionsMap: OverridePositionsMap): [OverridePosition, ComboGroupOverride, boolean]
  {
    let isOverrideExists = false;
    let comboGroupOverride: ComboGroupOverride = {};
    let overridePosition = overridePositionsMap.getOverride(PositionTypeEnum.ComboGroup, comboGroupId);

    if (!overridePosition){
      overridePosition = this.createComboGroupOverridePosition(comboGroupId, menuSectionId, emptyOverride);
    }
    else{
      comboGroupOverride = JSON.parse(overridePosition.override) as ComboGroupOverride;
      isOverrideExists = true;
    }

    return [overridePosition, comboGroupOverride, isOverrideExists];
  }

  createComboGroupOverridePosition(
    comboGroupId: string,
    menuSectionId: string,
    override: string): OverridePosition
  {
    return {
      positionId: comboGroupId,
      positionType: PositionTypeEnum.ComboGroup,
      menuSectionId: menuSectionId,
      override: override,
    } as OverridePosition;
  }

  copyOverridePositionsRequest(primaryСomboGroupId: string): OverridePositionRequest[]
  {
    let result: OverridePositionRequest[] = [];

    for (let [comboGroupId, comboGroup] of this.originalDictionaryComboGroups) {
      if (comboGroupId === primaryСomboGroupId || comboGroup.name !== this.originalComboGroup.name){
        continue;
      }

      let [comboGroupOverridePosition, comboGroupOverride, isOverrideExists] = this.findOrCreateComboGroupOverridePositionById(
        comboGroupId,
        this.menuSectionId,
        this.overridePositionsMap);

      let overrideInstructions = this.createOverrideInstructions(isOverrideExists, comboGroupOverride);

      if (!overrideInstructions.length) {
        return;
      }

      const overridePositionRequest: OverridePositionRequest = {
        ...comboGroupOverridePosition,
        instructions: overrideInstructions,
      };

      result.push(overridePositionRequest);
    }

    return result;
  }

  async buildComboProductsStructureRequest(){
    let batchOverridePositionsRequest: OverridePositionRequest[] = [];
    for (let comboItemStructure of this.comboProductStructures) {
      const initialComboProductStructure = this.getInitialComboProduct(comboItemStructure.externalProductId, comboItemStructure.sizeId);
      const originalComboProductStructure = this.getOriginalComboProduct(comboItemStructure.externalProductId, comboItemStructure.sizeId);

      if (!hasChangeComboStructure(comboItemStructure, initialComboProductStructure)){
        continue;
      }

      if(this.saveForAllCombo){
        const response = await this.productApiService.getAllComboProductStructures({
          menuId: this.menuSections[0].menuId,
          productStructures: [
            {
              productId: comboItemStructure.productId,
              sizeId: comboItemStructure.sizeId,
            }
          ]
        });

        const responseItems = response.data ?? [];

        for (let responseItem of responseItems) {
          const overridePositionRequest = buildOverridePositionRequest(
            this.menuSectionId,
            responseItem.parentComboProductId,
            responseItem.externalItemProductId,
            responseItem.sizeId,
            this.overridePositionsMap,
            comboItemStructure,
            initialComboProductStructure,
            originalComboProductStructure
          );
          batchOverridePositionsRequest.push(overridePositionRequest);
        }
      }
      else{
        const comboElementKey = getComboElementKey(this.comboGroupStructure.comboGroupId, comboItemStructure.externalProductId);
        let overridePositionRequest = buildOverridePositionRequest(
          this.menuSectionId,
          this.productId,
          comboElementKey,
          comboItemStructure.sizeId,
          this.overridePositionsMap,
          comboItemStructure,
          initialComboProductStructure,
          originalComboProductStructure
        );
        batchOverridePositionsRequest.push(overridePositionRequest);
      }
    }

    return batchOverridePositionsRequest;
  }

  isNameDifferent() {
    return this.originalComboGroup.name !== this.comboGroupName;
  }

  getInitialComboProduct(id: string, sizeId: string) {
    return this.initialComboProductStructures.find(p => p.externalProductId == id && p.sizeId == sizeId);
  }
  getOriginalComboProduct(id: string, sizeId: string) {
    return this.originalComboGroupStructure.products.find(p => p.externalProductId == id && p.sizeId == sizeId);
  }

  onChangeInformationMessage() {
    this.changes = [];
    if (this.initValues['comboGroupName'] != this.comboGroupName) {
      this.changes.push(`"Combo Group ${this.comboGroupName} Name"`)
    }
    this.changeInformationMessageEvent.emit(this.changes);
  }

  trackByFn(index, item: ComboProductStructure) {
    return item.externalProductId + item.sizeId;
  }

  showModifiers(){
    this.dialog.open(EditModifierGroupsModalComponent, {
      data: {}
    });
  }

  isNameTranslationDifferent(lang: string): boolean {
    const currentValue = this.nameTranslations[lang];
    const originalValue = this.originalComboGroup.nameTranslations[lang] ?? '';
    return currentValue != originalValue;
  }

  changeComboGroupNameTranslations(event: any, lang: string) {
    this.nameTranslations[lang] = event.target.value;
  }

  clearName() {
    this.comboGroupName = this.originalComboGroup.name;
  }

  clearNameTranslation(lang: string) {
    this.nameTranslations[lang] = this.originalComboGroup.nameTranslations[lang] ?? '';
  }
}
